<template>
  <v-app>
    <!-- Maintenance Overlay -->
    <v-overlay
      v-model="underMaintenance"
      persistent
      class="maintenance-overlay"
      :z-index="9999"
    >
      <div class="maintenance-content text-center">
        <v-icon size="80" color="amber">mdi-wrench</v-icon>
        <h1 class="mt-4 text-white">Site under maintenance</h1>
        <p class="mt-3 text-white text-h6">
          Estimated finish: {{ maintenanceFinishTime }} hours
        </p>
        <p class="mt-2 text-white text-body-1">Revenim in curand</p>
      </div>
    </v-overlay>

    <v-main style="padding-left: 0 !important; --v-layout-left: 0 !important;">
      <router-view/>
    </v-main>

    <!-- Cookie Consent Banner -->
    <CookieConsent />

    <!-- Payment result snackbar -->
    <v-snackbar
      v-model="showPaymentSnackbar"
      :color="paymentSuccess ? 'success' : 'error'"
      :timeout="5000"
      location="top"
    >
      {{ paymentMessage }}
      <template v-slot:actions>
        <v-btn variant="text" color="red" @click="showPaymentSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import CookieConsent from '@/components/CookieConsent.vue'
import { useMaintenanceCheck } from '@/composables/useMaintenanceCheck'

export default {
  name: 'App',
  components: {
    CookieConsent
  },
  data: () => ({
    showPaymentSnackbar: false,
    paymentSuccess: false,
    paymentMessage: '',
    underMaintenance: false,
    maintenanceActive: false,
    maintenanceFinishTime: '2',
    maintenanceInterval: null
  }),
  computed: {
    storeUser() {
      return this.$store.state.user
    }
  },
  watch: {
    storeUser() {
      // When user logs in (after refresh/loginWithToken), re-check exemption
      this.updateMaintenanceOverlay()
    }
  },
  methods: {
    handlePaymentMessage(event) {
      // Check if this is a payment complete message
      if (event.data && event.data.type === 'PAYMENT_COMPLETE') {
        this.paymentSuccess = event.data.success;
        this.paymentMessage = event.data.message;
        this.showPaymentSnackbar = true;

        // Dispatch custom event for components to listen to
        window.dispatchEvent(new CustomEvent('payment-complete', {
          detail: {
            success: event.data.success,
            message: event.data.message
          }
        }));
      }
    },
    isMaintenanceExempt() {
      const roles = this.$store.getters.getRoles || []
      const exemptRoles = ['root', 'admin', 'test_under_maintenance']
      return roles.some(r => exemptRoles.includes(r))
    },
    async checkMaintenance() {
      const { checkMaintenanceStatus } = useMaintenanceCheck()
      const status = await checkMaintenanceStatus()
      this.maintenanceActive = status.underMaintenance
      this.maintenanceFinishTime = status.finishTime
      this.updateMaintenanceOverlay()
    },
    updateMaintenanceOverlay() {
      if (this.maintenanceActive && this.isMaintenanceExempt()) {
        this.underMaintenance = false
      } else {
        this.underMaintenance = this.maintenanceActive
      }
    }
  },
  async mounted() {
    // Listen for postMessage from payment window
    window.addEventListener('message', this.handlePaymentMessage);

    // Check maintenance status
    await this.checkMaintenance()

    // Poll every 60 seconds
    this.maintenanceInterval = setInterval(() => {
      this.checkMaintenance()
    }, 60000)
  },
  beforeUnmount() {
    window.removeEventListener('message', this.handlePaymentMessage);
    if (this.maintenanceInterval) {
      clearInterval(this.maintenanceInterval)
    }
  }
}
</script>

<style>
/* Force full width layout - no reserved space for drawers */
* {
  box-sizing: border-box;
}

html, body {
  margin: 0 !important;
  padding: 0 !important;
  overflow-x: auto !important;
  width: 100% !important;
  font-size: 16px !important;
}

/* Increase font size by 2 points globally */
.v-application {
  font-size: 16px !important;
}

.v-table td, .v-table th,
.v-card-text, .v-card-title, .v-card-subtitle,
.v-list-item-title, .v-list-item-subtitle,
.v-btn, .v-chip {
  font-size: 16px !important;
}

.v-text-field input, .v-select .v-field__input, .v-autocomplete input {
  font-size: 16px !important;
}

h1 { font-size: 2rem !important; }
h2 { font-size: 1.75rem !important; }
h3 { font-size: 1.5rem !important; }

#app {
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  min-width: 100% !important;
}

.v-application {
  --v-layout-left: 0 !important;
  --v-layout-right: 0 !important;
  min-width: 100% !important;
}

.v-application__wrap {
  min-width: 100% !important;
}

.v-main {
  --v-layout-left: 0 !important;
  --v-layout-right: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  padding-top: 0 !important;
  width: 100% !important;
  min-width: 100% !important;
  margin-left: 0 !important;
}

.v-main__wrap {
  margin-left: 0 !important;
  padding-left: 0 !important;
  width: 100% !important;
}

.v-layout {
  --v-layout-left: 0 !important;
  --v-layout-right: 0 !important;
}

.maintenance-overlay .v-overlay__content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.maintenance-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
